export enum UserType {
  INTERNAL = "internal",
  EXTERNAL = "external",
}

export enum V5Origin {
  SERVICE = "v5-service-site",
  EMPLOYER = "v5-employer",
}

export type CredentialResponse = {
  id: string;
  email: string;
  username: string | null;
  partition: Partition;
  emailVerified: boolean;
  lastLoginAt: Date | null;
  emailDuplicated: boolean;
  mfaEnabled: MfaEnabled;
  sqaForceReset: boolean;
  passwordForceReset: boolean;
  profiles?: Array<Profile>;
  requireUserAction?: boolean;
  questions?: Array<Question>;
  temporaryPassword?: string;
  blocks?: Array<CredentialBlock>;
  auth0Identifiers?: Array<Auth0Identifier>;
  mfaFactors?: Array<MFAFactor>;
};

export enum Partition {
  MEMBER = "MEMBER",
  NON_MEMBER = "NON_MEMBER",
  OKTA = "OKTA"
};

export type MFAFactor = {
  id: string;
  type: "email" | "sms" | "voice" | "recovery-code";
  confirmed: boolean;
  name: string;
};

export type Auth0Identifier = {
  id: string;
  type: "SAML" | "AUTH0";
};

export type LoginSupportInfoFormActions = {
  passwordResetEmail: FormActionStatus;
  resetPhone: FormActionStatus;
  sendVerificationEmail: FormActionStatus;
  verifyOrUpdateEmail: FormActionStatus;
  updateUsername: FormActionStatus;
};

export enum MfaEnabled {
  YES = "YES",
  NO = "NO",
  ONLY_EMAIL = "ONLY_EMAIL",
}

export const mfaEnabledMap = new Map<MfaEnabled, string>([
  [MfaEnabled.YES, "Yes"],
  [MfaEnabled.NO, "No"],
  [MfaEnabled.ONLY_EMAIL, "Only email"],
]);

export type Profile = {
  id: string;
  firstName: string;
  lastName: string;
};

export type UpdateUserCredentialsRequest = {
  id: string;
  email?: string;
  username?: string;
  questions?: QuestionAnswer[];
};

export type QuestionAnswer = {
  id?: string;
  question?: string
  questionId?: string;
  answer?: string;
  credentialId?: string;
};

export type Question = {
  id: string;
  questionId: string | null;
  question: string;
};

export type PredefinedQuestion = {
  id: string;
  question: string;
};

export type CredentialBlock = {
  id: string;
  type: BlockReasonTypes;
  createdBy?: string;
  blockedAt: Date;
  info?: string | null;
};

export enum BlockReasonTypes {
  FRAUD = "FRAUD",
  BRUTE_FORCE = "BRUTE_FORCE",
  PASSWORD_BREACH = "PASSWORD_BREACH",
}

export enum BlockReasonTypesProperties {
  FRAUD = "fraud",
  BRUTE_FORCE = "bruteForce",
  PASSWORD_BREACH = "passwordBreach",
}

export enum BlockReasonText {
  FRAUD = "Fraud Block",
  BRUTE_FORCE = "Blocked for too many attempts",
  PASSWORD_BREACH = "Breached Detection",
}

export enum SendingEmailStatus {
  NO_ACTION = "No Action",
  SENDING = "Sending Email",
  SENT = "Email Sent",
  ERROR = "Something went wrong",
}

export enum ResetingFieldStatus {
  NO_ACTION = "No Action",
  RESETING = "Reseting",
  RESET = "Reset",
  ERROR = "Something went wrong",
}

export enum FormActionStatus {
  NO_ACTION = "NO_ACTION",
  IN_PROGRESS = "IN_PROGRESS",
  DONE = "DONE",
  ERROR = "ERROR",
}

export type UserActivity = {
  id: string;
  credentialId: string;
  type: string;
  description: string;
  application: Application;
  executedBy: string;
  executedAt: string;
  auth0LogId: string;
  executedByName: string;
  partition: string;
};

export enum Application {
  WEB = "Web",
  MOBILE = "Mobile",
}

export type UserActivityResponse = {
  items: Array<UserActivity>;
  count: number;
};

export type ChangeLog = {
  id: string;
  credentialId: string;
  type: string;
  oldValue: string;
  newValue: string;
  executedAt: string;
  executedBy: string;
  executedByName: string;
  partition: string;
};

export type ChangeLogResponse = {
  items: Array<ChangeLog>;
  count: number;
};

export const freeFormQuestionId = "free-form-question-id";

export enum CredentialsStatusActions {
  sqaMissing = "sqaMissing",
  sqaIncomplete = "sqaIncomplete",
  sqaForceUpdate = "sqaForceUpdate",
  sqaRequiredResponse = "sqaRequiredResponse",
  emailMissing = "emailMissing",
  emailVerified = "emailVerified",
  emailDuplicated = "emailDuplicated",
  passwordForceReset = "passwordForceReset",
  passwordMissing = "passwordMissing",
  usernameMissing = "usernameMissing",
  usernameForceUpdate = "usernameForceUpdate",
}

export const credentialsStatusMap = new Map<CredentialsStatusActions, string>([
  [CredentialsStatusActions.sqaMissing, "Security Q&A missing"],
  [CredentialsStatusActions.sqaIncomplete, "Security Question incomplete"],
  [
    CredentialsStatusActions.sqaForceUpdate,
    "Security Question change required on next login",
  ],
  [
    CredentialsStatusActions.sqaRequiredResponse,
    "Security Question is required",
  ],
  [CredentialsStatusActions.emailMissing, "Missing email"],
  [CredentialsStatusActions.emailVerified, "Email is not verified"],
  [CredentialsStatusActions.emailDuplicated, "Duplicate email found"],
  [
    CredentialsStatusActions.passwordForceReset,
    "Password change required on next login",
  ],
  [CredentialsStatusActions.passwordMissing, "Password is missing"],
  [CredentialsStatusActions.usernameMissing, "Username is missing"],
  [
    CredentialsStatusActions.usernameForceUpdate,
    "Username change required on next login",
  ],
]);

export type PendingActions = {
  require: Array<CredentialsStatusActions>;
  optional: Array<CredentialsStatusActions>;
};

export enum UserPermissions {
  FRAUD_BLOCK_EDIT = "/ProfileAdmin/FraudBlock/Edit",
  BRUTE_FORCE_BLOCK_EDIT = "/ProfileAdmin/TooManyAttempts/Edit",
  //
  VERIFY_EMAIL_ADDRESS_EDIT = "/ProfileAdmin/VerifyEmailAddress/Edit",
  CHANGE_EMAIL_EDIT = "/ProfileAdmin/ChangeEmail/Edit",
  SEND_VERIFY_EMAIL_EDIT = "/ProfileAdmin/SendVerifyEmail/Edit",
  //
  GENERATE_TEMPORARY_PASSWORD_EDIT = "/ProfileAdmin/GenerateTempPassword/Edit",
  SEND_RESET_PASSWORD_EMAIL = "/ProfileAdmin/SendResetPasswordEmail/Edit",
  //
  CREDENTIALS_VIEW = "/ProfileAdmin/View",
  //
  RESET_REMOVE_MFA_PHONE_EDIT = "/ProfileAdmin/ResetRemoveMFAPhone/Edit",
  //
  SQA_EDIT = "/ProfileAdmin/ChangeSecurityQuestionAnswer/Edit",
  SQA_VIEW = "/ProfileAdmin/ViewSecurityAnswer/Edit",
  FORCE_SQA_UPDATE_EDIT = "/ProfileAdmin/ForceUpdateSecurityQuestion/Edit",
  //
  UPDATE_USERNAME_EDIT = "/ProfileAdmin/UpdateUsername/Edit",
  FORCE_UPDATE_USERNAME_EDIT = "/ProfileAdmin/ForceUpdateUsernane/Edit",
  //
  ADMIN_SECTION_VIEW = "/ProfileAdmin/AdminSection/View",
}

export type Brand = {
  logoUrl: string;
  displayHealthEquityLogo: boolean;
  customFooterHtml: string[];
  actionItemColor: string;
  actionItemHoverColor: string | null;
  healthEquityLogoPosition: string;
};

export type EncodedFields = "password" | "answer";